import {
  faCheck,
  faChevronDown,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { motion } from 'framer-motion'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useForm, useFormState, UseFormReturn } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { StepProps } from '../../Step'
import { StepTitleProps } from './StepperHeader'

type Props = {
  form?: UseFormReturn<any>
  currentStep: number
  stepPropsList: StepProps[]
  steps: (React.ReactNode | false)[]
  onClickTitle: (stepIndex: number) => void
  /** When true, every step header is clickable regardless of validation
   * or completion state. Used by edit flows that need to let the user
   * jump around freely. */
  allowJumpToAnyStep?: boolean
  /** When true, steps with index greater than `maxStepReached` are not
   * rendered at all — they reveal as the user advances. */
  hideUntouched?: boolean
  maxStepReached?: number
}

/** Vertical accordion variant of the Stepper header. Each step renders
 * as an accordion item with the same complete/current/upcoming/error
 * states as `DesktopStepTitle` from the tabs variant; only the current
 * step's panel is expanded. */
export const StepperAccordion = (props: Props) => {
  const baseForm = useForm()
  const form = props.form || baseForm

  const { isValid } = useFormState({
    control: form.control,
    name: props.stepPropsList[props.currentStep]?.fields,
  })

  const getTitleState = (titleIndex: number): StepTitleProps['type'] => {
    const stepProps = props.stepPropsList[titleIndex]
    if (stepProps?.errors?.length) return 'error'
    if (props.currentStep === titleIndex) return 'current'
    if (props.stepPropsList[titleIndex]?.completed) return 'complete'
    return 'upcoming'
  }

  const isDisabled = (index: number, titleProps: StepProps) => {
    // Free jumping still honors a step's own `disabled` flag, so a wizard can
    // show every step yet lock the ones whose prerequisites aren't met. Steps
    // that don't set `disabled` stay open (backward compatible).
    if (props.allowJumpToAnyStep) return !!titleProps?.disabled
    const currentStepProps = props.stepPropsList[props.currentStep]
    const state = getTitleState(index)
    if (state === 'complete') return false
    if (props.form && !isValid) return true
    if (currentStepProps?.disabled) return true
    if (titleProps?.errors?.length) return false
    if (currentStepProps?.errors?.length) return true
    if (index < props.currentStep) return false
    if (props.stepPropsList[index]?.disabled) return false
    if (index !== props.currentStep + 1) return true
    return false
  }

  // Fall back to the current step (not the step count) so a standalone
  // accordion with `hideUntouched` still hides not-yet-reached steps;
  // `Stepper` always passes the real high-water mark.
  const maxReached = props.maxStepReached ?? props.currentStep

  return (
    <div className='w-full grow min-h-0 overflow-y-auto p-2 flex flex-col gap-2'>
      {props.stepPropsList.map((stepProps, i) => {
        if (props.hideUntouched && i > maxReached) return null
        return (
          <AccordionStepItem
            key={i}
            index={i}
            isOpen={i === props.currentStep}
            state={getTitleState(i)}
            disabled={isDisabled(i, stepProps)}
            stepProps={stepProps}
            stepContent={props.steps[i]}
            onClick={() => props.onClickTitle(i)}
          />
        )
      })}
    </div>
  )
}

type AccordionStepItemProps = {
  index: number
  isOpen: boolean
  state: StepTitleProps['type']
  disabled: boolean
  stepProps: StepProps
  stepContent: React.ReactNode | false
  onClick: () => void
}

const AccordionStepItem = ({
  index,
  isOpen,
  state,
  disabled,
  stepProps,
  stepContent,
  onClick,
}: AccordionStepItemProps) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number | 'auto'>('auto')

  // Measure the panel's natural height with a ResizeObserver so the
  // accordion grows when inner content does (e.g. revealing a field
  // under a switch). Without this, framer-motion freezes the height
  // at the value captured when the accordion first opens, clipping
  // anything that appears later.
  useLayoutEffect(() => {
    // Only the open panel needs measuring; observing every closed panel
    // wastes a ResizeObserver per step and (with empty deps) never tears
    // down. Re-running on `isOpen` installs/disconnects it as steps toggle.
    if (!isOpen) return
    const el = panelRef.current
    if (!el) return
    const measure = () => setMeasuredHeight(el.scrollHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isOpen])

  // After the open-animation completes, drop the height to `auto`
  // so the panel can still flex with content changes that happen
  // outside the ResizeObserver path (e.g. images loading).
  const [animationDone, setAnimationDone] = useState(isOpen)
  // `animationDone` is a latch flipped true only by `onAnimationComplete`
  // (an imperative event, not derivable from `isOpen`); this effect just
  // re-arms it on close so the next open re-animates from `measuredHeight`
  // instead of snapping straight to `auto`.
  useEffect(() => {
    if (!isOpen) setAnimationDone(false)
  }, [isOpen])

  return (
    <div
      className={twMerge(
        // `shrink-0` is load-bearing — without it the accordion items
        // are flex children that can collapse below their natural
        // height when the parent overflows, which combined with the
        // `overflow-hidden` below would clip the header.
        'shrink-0 border rounded-lg bg-white overflow-hidden transition-colors',
        isOpen && 'border-indigo-400 shadow-sm',
        state === 'error' && 'border-red-300',
      )}>
      <AccordionStepHeader
        stepNumber={index + 1}
        title={stepProps.title}
        type={state}
        disabled={disabled}
        isOpen={isOpen}
        onClick={() => {
          if (disabled) return
          onClick()
        }}
      />
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? (animationDone ? 'auto' : measuredHeight) : 0,
        }}
        transition={{ duration: 0.25 }}
        onAnimationComplete={() => {
          if (isOpen) setAnimationDone(true)
        }}
        className='overflow-hidden'>
        <div
          ref={panelRef}
          className={twMerge('border-t', stepProps.className)}>
          {stepContent}
        </div>
      </motion.div>
    </div>
  )
}

type HeaderProps = {
  stepNumber: number
  title: string
  type: StepTitleProps['type']
  disabled?: boolean
  isOpen?: boolean
  onClick: () => void
}

const AccordionStepHeader = ({
  stepNumber,
  title,
  type,
  disabled,
  isOpen,
  onClick,
}: HeaderProps) => {
  const indexContainerClasses =
    'w-8 h-8 flex items-center justify-center rounded-full border-2 shrink-0'
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      aria-expanded={isOpen}
      className={twMerge(
        'flex items-center w-full px-3 py-2 gap-3 text-left',
        disabled && 'opacity-60 cursor-not-allowed',
        !disabled && 'hover:bg-base-50',
      )}>
      {type === 'current' && (
        <span
          className={`${indexContainerClasses} text-indigo-600 border-indigo-600`}>
          <span className='h-6 w-6 flex items-center justify-center'>
            {stepNumber}
          </span>
        </span>
      )}
      {type === 'complete' && (
        <span
          className={`${indexContainerClasses} border-none bg-indigo-600`}>
          <FontAwesomeIcon icon={faCheck} className='w-4! h-4! text-white' />
        </span>
      )}
      {type === 'error' && (
        <span
          data-testid='error-icon'
          className={`${indexContainerClasses} border-2 border-red-600`}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className='w-4! h-4! text-red-600'
          />
        </span>
      )}
      {type === 'upcoming' && (
        <span className={`${indexContainerClasses} border-2 border-gray-300`}>
          <span className='text-base-500'>{stepNumber}</span>
        </span>
      )}

      <span
        className={twMerge(
          'text-sm font-medium grow truncate',
          type === 'current' && 'text-indigo-600',
          type === 'complete' && 'text-base-900',
          type === 'error' && 'text-red-600',
          type === 'upcoming' && 'text-base-500',
        )}>
        {title}
      </span>

      <FontAwesomeIcon
        icon={faChevronDown}
        className={twMerge(
          'h-4 w-4 transition-transform duration-200 text-base-400',
          isOpen && 'rotate-180',
        )}
      />
    </button>
  )
}
